import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  Subject,
  takeUntil,
  debounceTime,
  concatMap,
  from,
  groupBy,
  mergeMap,
} from "rxjs";
import {
  IAppPlugin,
  IAppPluginWidget,
  IAppDashboard,
  ICreateAppPluginWidget,
  IUser,
  LayoutMode,
  LayoutModeService,
  ModelService,
  IPluginPermissions,
  PluginInjectorService,
  IModuleWidget,
  PluginInjectorWidget,
  SessionService,
  WidgetPermissions,
  WidgetActionRegistry,
  ModuleWidgetRegistry,
  WidgetDialogService,
  WidgetInitSynchronizer,
  WidgetDataStore,
  WidgetGridLayoutConfig,
  PermissionLoader,
} from "core-client/api";
import { SharedModule } from "../../../shared/shared.module";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { SwiperOptions } from "swiper/types";
import { SwiperDirective } from "../../../shared/swiper.directive";
import { WidgetHeaderComponent } from "../../../shared/widget-header/widget-header.component";
import { WidgetHostRegistry } from "../../../shared/widget-host-registry.service";
import { DashboardWidgetLoader } from "../../../shared/dashboard-widget-loader.service";
import { WidgetWindowService } from "../../../services/widget-window.service";
import { WidgetWindowPlaceholderComponent } from "./widget-window-placeholder.component";
import { WidgetScreen } from "../../../services/widget-screen";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  imports: [SharedModule, SwiperDirective, WidgetHeaderComponent, WidgetWindowPlaceholderComponent],
  providers: [WidgetHostRegistry],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ["./main.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChildren("widgetItems", { read: ElementRef })
  widgetItems!: QueryList<ElementRef>;
  widgetInFullscreen: boolean = false;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  private readonly boundOnWidgetFullscreenChange =
    this.onWidgetFullscreenChange.bind(this);
  private readonly layoutMode = inject(LayoutModeService);
  isMobileView: boolean = this.layoutMode.isMobile;
  private readonly widgetHosts = inject(WidgetHostRegistry);
  @Input() loggedUser: IUser | undefined;
  private permissions: IPluginPermissions[] = [];
  private widgetHeaderDisplayTimeout: ReturnType<typeof setTimeout> | null =
    null;
  // Held by id, not on the widget: a rebuild replaces those objects.
  private readonly menuOpenWidgetIds = new Set<string>();
  private loadedDashboardId: string | null = null;

  // swiper config
  swiperConfig: SwiperOptions = {
    initialSlide: 0,
    spaceBetween: 10,
    pagination: { clickable: true },
    navigation: false, // Show navigation arrows
    virtual: false,
  };

  constructor(
    public modelService: ModelService,
    private readonly sessionService: SessionService,
    public dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    public widgetPermissions: WidgetPermissions,
    private readonly widgetActionRegistry: WidgetActionRegistry,
    private readonly moduleWidgetRegistry: ModuleWidgetRegistry,
    public widgetDialogService: WidgetDialogService,
    private readonly widgetInitSynchronizer: WidgetInitSynchronizer,
    public widgetDataStore: WidgetDataStore,
    public widgetGridLayoutConfig: WidgetGridLayoutConfig,
    private readonly permissionLoader: PermissionLoader,
    private readonly widgetLoader: DashboardWidgetLoader,
    readonly widgetWindows: WidgetWindowService,
  ) { }

  ngOnInit(): void {
    this.widgetWindows.start();
    this.layoutMode.mode$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((mode: LayoutMode) => {
        const mobile = mode === "mobile";
        if (mobile === this.isMobileView) return;
        this.widgetHosts.detachAll();
        this.isMobileView = mobile;
      });

    this.sessionService.dashboard
      .observeActiveAppDashboard$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (dashboard: IAppDashboard) => {
        const user = this.sessionService.user.getUser();
        if (!user) return;
        const started = performance.now();
        try {
          const [permissions, appPluginWidgets, appPlugins] = await Promise.all([
            this.permissionLoader.load(dashboard, user),
            this.modelService._user.listAllAppPluginWidgets(user._id),
            this.modelService._appPlugin.listAll(),
          ]);
          if (this.sessionService.user.getUser() === null) return;
          this.permissions = permissions;
          this.loadDashboardWidgets(dashboard._id, appPluginWidgets, appPlugins);
          console.debug(
            `[Dashboard] "${dashboard.name}": ${this.widgetDataStore.getWidgets().length} widgets ready in ${Math.round(performance.now() - started)} ms`,
          );
        } catch (err) {
          if (this.sessionService.user.getUser() === null) return;
          throw err;
        }
      });

    this.widgetDataStore.add$
      .pipe(
        concatMap((widgetItem: PluginInjectorWidget.IWidgetAddAction) =>
          from(this.handleAddSafe(widgetItem)),
        ),
        takeUntil(this.ngUnsubscribe$),
      )
      .subscribe();

    this.widgetDataStore.addError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ name, error }) => {
        console.error(`Failed to add widget "${name}":`, error);
        const message = this.translate.instant(
          "CORE_PLUGIN.WIDGET.ADD_FAILED",
          { name },
        );
        const close = this.translate.instant("CLOSE");
        this.snackBar.open(message, close, { duration: 6000 });
      });

    this.widgetDataStore.sizeChanged$
      .pipe(
        // Per-widget debounce — a single debounceTime would drop all but the last burst emission.
        groupBy((w: IAppPluginWidget) => w._id),
        mergeMap((g) => g.pipe(debounceTime(300))),
        takeUntil(this.ngUnsubscribe$),
      )
      .subscribe(async (appPluginWidget: IAppPluginWidget) => {
        try {
          await this.modelService._appPluginWidget.upsertOne(appPluginWidget);
        } catch (error) {
          console.error("Failed to save widget layout:", error);
        }
      });

    this.widgetDataStore.edit$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((unlocked: boolean) => {
        this.widgetGridLayoutConfig.options = {
          ...this.widgetGridLayoutConfig.options,
          draggable: { enabled: unlocked },
          resizable: { enabled: unlocked },
        };
      });

    this.widgetDataStore.delete$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (_id: string) => {
        // delete appPluginWidget from db
        await this.modelService._appPluginWidget.deleteById(_id);
      });

    // listen for widget fullscreen change events
    document.addEventListener(
      "fullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "webkitfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "mozfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "msfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
  }

  ngOnDestroy(): void {
    // cleanup widget fullscreen event listeners
    document.removeEventListener(
      "fullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "webkitfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "mozfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "msfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    this.widgetDataStore.unsubscribeAll();
  }

  private onWidgetFullscreenChange() {
    this.widgetInFullscreen = !!document.fullscreenElement;
    if (this.widgetInFullscreen && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        for (const widget of this.widgetDataStore.getWidgets()) {
          widget.isHeaderHovered = false;
        }
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    } else if (!this.widgetInFullscreen) {
      this.clearWidgetHeaderDisplayTimeout();
    }
  }

  async removeWidgetItem(
    $event: MouseEvent | TouchEvent,
    item: PluginInjectorWidget.IWidget,
    swiperContainerRef?: ElementRef,
  ): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();

    if (item._id) {
      // Extra additional action after removing the widget of a plugin
      if (item.pluginModule === "MAP") {
        const ngsiSubscriptionWidgets =
          await this.modelService._ngsiSubscriptionWidget.listAll(
            `app_plugin_widget=${item.appPluginWidget._id}`,
          );

        await this.widgetActionRegistry.execute(
          item,
          this.modelService,
          ngsiSubscriptionWidgets,
        );
      }

      this.widgetDataStore.removeWidgetItemById(item._id);
    }

    if (swiperContainerRef) {
      swiperContainerRef.nativeElement.swiper.update();
    }
  }

  private loadDashboardWidgets(
    dashboardId: string,
    appPluginWidgets: IAppPluginWidget[],
    appPlugins: IAppPlugin[],
  ): void {
    const built = this.widgetLoader.buildWidgets(
      appPluginWidgets,
      appPlugins,
      this.loggedUser,
      this.permissions,
    );
    const sameDashboard = dashboardId === this.loadedDashboardId;
    this.loadedDashboardId = dashboardId;
    this.widgetDataStore.setWidgets(
      sameDashboard ? this.widgetLoader.keepPlaced(this.widgetDataStore.getWidgets(), built) : built,
    );
  }

  openInWindow(widget: PluginInjectorWidget.IWidget, screen?: WidgetScreen): void {
    if (this.widgetWindows.open(widget, screen)) {
      return;
    }
    this.snackBar.open(
      this.translate.instant("APP.WIDGET_WINDOW_BLOCKED"),
      this.translate.instant("CLOSE"),
      { duration: 6000 },
    );
  }

  private async handleAddSafe(
    widgetItem: PluginInjectorWidget.IWidgetAddAction,
  ): Promise<void> {
    try {
      await this.addNewPluginWidget(widgetItem);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.widgetDataStore.addError$.next({
        name: widgetItem.name,
        error: err,
      });
    }
  }

  private async addNewPluginWidget(
    widgetItem: PluginInjectorWidget.IWidgetAddAction,
  ): Promise<void> {
    let widgetLayoutConfig: PluginInjectorWidget.IWidgetDefaultLayoutConfig =
      this.widgetGridLayoutConfig.getDefaultLayoutConfig();
    const registerModuleWidgets: IModuleWidget[] =
      this.moduleWidgetRegistry.getModuleWidgets();

    const gridsterApi = this.widgetGridLayoutConfig.options?.["api"];
    if (gridsterApi?.getFirstPossiblePosition) {
      const positioned = gridsterApi.getFirstPossiblePosition({
        x: widgetLayoutConfig.x,
        y: widgetLayoutConfig.y,
        cols: widgetLayoutConfig.cols,
        rows: widgetLayoutConfig.rows,
      });
      widgetLayoutConfig = {
        ...widgetLayoutConfig,
        x: positioned.x,
        y: positioned.y,
      };
    }

    const widgetModule = registerModuleWidgets.find(
      (moduleWidget: IModuleWidget) =>
        moduleWidget.pluginId === widgetItem.pluginId &&
        moduleWidget.pluginModule === widgetItem.pluginModule,
    );

    const newAppPluginWidget: ICreateAppPluginWidget = {
      enabled: true,
      name: widgetItem.name,
      app_plugin: widgetItem.appPluginId,
      plugin_module: widgetItem.pluginModule,
      settings: {
        ...widgetModule?.initialSettings,
        widgetHeader: {
          alwaysDisplayed: true,
          onHoverDisplayed: false,
          alwaysOff: false,
          transparent: false,
        },
        showWidgetTitle: true,
      },
      layout: {
        position_x: widgetLayoutConfig.x,
        position_y: widgetLayoutConfig.y,
        columns: widgetLayoutConfig.cols,
        rows: widgetLayoutConfig.rows,
        width_px: widgetLayoutConfig.width,
        height_px: widgetLayoutConfig.height,
      },
    };

    const appPluginWidget =
      await this.modelService._appPluginWidget.upsertOneAndFetch(
        newAppPluginWidget,
        "",
      );

    if (this.loggedUser) {
      this.pluginInjectorService.applyExtraData(
        appPluginWidget,
        this.loggedUser,
      );
    }

    const widget: PluginInjectorWidget.IWidget = {
      _id: appPluginWidget._id,
      name: appPluginWidget.name,
      x: appPluginWidget.layout.position_x,
      y: appPluginWidget.layout.position_y,
      rows: appPluginWidget.layout.rows,
      cols: appPluginWidget.layout.columns,
      height: appPluginWidget.layout.height_px,
      width: appPluginWidget.layout.width_px,
      pluginId: widgetItem.pluginId,
      pluginModule: String(appPluginWidget.plugin_module),
      appPluginWidget: appPluginWidget,
      isHeaderHovered: false,
    };

    if (widgetModule) {
      widget.widgetPermissions = widgetModule.widgetPermissions;
    }

    if (this.widgetLoader.isWidgetEnabled(widget, this.permissions)) {
      this.widgetDataStore.addWidgetItem(widget);
      await this.widgetInitSynchronizer.waitForWidgetRegistered(widget._id);
    }
  }

  widgetSettingsEnabled(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find(
      (pluginPermissions: IPluginPermissions) =>
        pluginPermissions.plugin_id === widget.pluginId,
    );

    if (widgetPermissions) {
      return this.widgetPermissions.check(
        widget,
        widgetPermissions,
        "settingsEnabled",
      );
    } else {
      return false;
    }
  }

  private widgetHeaderPermitted(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find(
      (pluginPermissions: IPluginPermissions) =>
        pluginPermissions.plugin_id === widget.pluginId,
    );
    return widgetPermissions
      ? this.widgetPermissions.check(widget, widgetPermissions, "showHeader")
      : true;
  }

  // a hover header stays in the DOM so that showing and hiding can both transition
  renderWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const header = widget.appPluginWidget.settings.widgetHeader;
    return (
      this.widgetHeaderPermitted(widget) &&
      (header.alwaysDisplayed || header.onHoverDisplayed)
    );
  }

  showWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const header = widget.appPluginWidget.settings.widgetHeader;
    return (
      this.widgetHeaderPermitted(widget) &&
      (header.alwaysDisplayed ||
        (header.onHoverDisplayed && widget.isHeaderHovered))
    );
  }

  // Only an in-flow header needs separating from the content; the hover header overlays it.
  showWidgetDivider(widget: PluginInjectorWidget.IWidget): boolean {
    return (
      this.showWidgetHeader(widget) &&
      widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed
    );
  }

  private clearWidgetHeaderDisplayTimeout(): void {
    if (this.widgetHeaderDisplayTimeout != null) {
      clearTimeout(this.widgetHeaderDisplayTimeout);
      this.widgetHeaderDisplayTimeout = null;
    }
  }

  onWidgetHover(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = true;
    if (
      (this.isMobileView || this.widgetInFullscreen) &&
      this.widgetHeaderDisplayTimeout == null
    ) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        if (this.menuOpenWidgetIds.has(widget._id)) return;
        widget.isHeaderHovered = false;
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  onWidgetHoverCancel(widget: PluginInjectorWidget.IWidget): void {
    // The header carries the open menu, and reaching that menu means leaving the widget.
    if (this.menuOpenWidgetIds.has(widget._id)) return;
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onWidgetMenuOpenChange(
    widget: PluginInjectorWidget.IWidget,
    open: boolean,
  ): void {
    if (open) {
      this.menuOpenWidgetIds.add(widget._id);
      widget.isHeaderHovered = true;
      return;
    }
    this.menuOpenWidgetIds.delete(widget._id);
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onSwiperSlideChange(): void {
    // set the 'isHeaderHovered' flag to false on all widgets, since in mobile mode only one widget can be displayed at the same time and on slide change any hovering is canceled
    for (const widget of this.widgetDataStore.getWidgets()) {
      widget.isHeaderHovered = false;
    }
    this.clearWidgetHeaderDisplayTimeout();
  }

  async toggleFullscreen(widgetIndex: number): Promise<void> {
    if (!document.fullscreenEnabled) {
      console.warn("Fullscreen API is not supported in this browser.");
      return;
    }

    // Get the widget at this index
    const widgets = this.widgetDataStore.getWidgets();
    const widget = widgets[widgetIndex];

    // Check if a plugin has registered a fullscreen interceptor for this widget
    if (widget) {
      const interceptor = this.pluginInjectorService.tryPluginFullscreen(
        widget.pluginModule,
        "addboard",
      );
      if (interceptor) {
        try {
          const handled = await interceptor.toPromise();
          if (handled) {
            console.log("[Dashboard] Video fullscreen toggled by phone widget");
            return;
          }
        } catch (error) {
          console.warn(
            "[Dashboard] Phone widget did not handle video fullscreen, falling back to widget fullscreen",
          );
        }
      }
    }

    // Fallback: Do normal widget fullscreen
    const element = this.widgetItems.toArray()[widgetIndex].nativeElement;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode: ${error.message} (${error.name})`,
          );
        });
        /* Firefox */
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for Firefox: ${error.message} (${error.name})`,
          );
        });
        /* Chrome, Safari and Opera */
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for WebKit: ${error.message} (${error.name})`,
          );
        });
        /* IE/Edge */
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for IE/Edge: ${error.message} (${error.name})`,
          );
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode: ${error.message} (${error.name})`,
          );
        });
        /* Firefox */
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for Firefox: ${error.message} (${error.name})`,
          );
        });
        /* Chrome, Safari and Opera */
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for WebKit: ${error.message} (${error.name})`,
          );
        });
        /* IE/Edge */
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for IE/Edge: ${error.message} (${error.name})`,
          );
        });
      }
    }
  }

  widgetHeaderIsHovered(widget: PluginInjectorWidget.IWidget): boolean {
    return (
      widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed &&
      widget.isHeaderHovered
    );
  }
}
